import {Box, Button, Typography} from "@mui/material";
import React, {useMemo, useState} from "react";
import {grey} from "@mui/material/colors";
import {useConfirmationContext} from "@/contexts/ConfirmationContext";
import {useNotificationContext} from "@/contexts/NotificationContext";
import {DataGridSimpleStyled, GridColDef} from "@/components/Shared/DataGridSimple";
import {LoadingDotBox} from "@/components/Shared/LoadingBox";
import {acceptInvitation, resolveInvitationDetail, resolveInvitedTeam} from "@/services/team";
import {getCustomerVerificationSteps} from "@/services/customer";
import Utils from "@/components/Shared/Utils";

function Invitation({invitation, close}: any) {
    const {setNotification} = useNotificationContext()
    const {setConfirmation} = useConfirmationContext()

    const {data: team} = resolveInvitationDetail(invitation?.metadata?.owner)

    async function handleSubmitInvitationAccepted() {
        const result = await acceptInvitation({token: invitation?.invite_token})

        if (result.member?.invite_token === null) {
            setNotification({
                message: `Welcome to the ${team.name} team! You can manage your joined teams, including ${team.name}, in the 'Invited Teams' section.`,
                open: true
            })
            close()
        }
    }

    return (
        <Box sx={{mt: 2, p: 2, bgcolor: grey[100], borderRadius: 1}}>
            <Typography fontSize={13} sx={{mb: 2}}>
                You have been invited to join a team! This team offers access to valuable resources. Please review the
                invitation for more details.
            </Typography>
            <Box sx={{mb: 1}}>
                <Typography fontSize={13}>Team Name</Typography>
                <Typography fontSize={13}
                            fontWeight={700}>{team?.name} {team?.organization && `(${team?.organization})` || ''}</Typography>
            </Box>
            <Box sx={{mb: 3}}>
                <Typography fontSize={13}>Owner</Typography>
                <Typography fontSize={13} fontWeight={700}>{team?.owner}</Typography>
            </Box>
            <Button
                variant="outlined"
                sx={{bgcolor: 'white ! important'}}
                onClick={() => setConfirmation({
                    dialog: {
                        title: `Join Team ${team?.name}`,
                        description: `Clicking to join allows your team owner to see your profile, the services you provide, and a log of your activity within the team. You can always manage your privacy settings later.`,
                    },
                    show: true,
                    callback: () => handleSubmitInvitationAccepted()
                })}
            >
                Join Team
            </Button>
        </Box>
    )
}

export default function TeamInvited({user, invitation}: any) {
    const {data, refetch} = resolveInvitedTeam(user)
    const {refetch: refetchCustomerSegment} = getCustomerVerificationSteps()

    const [filter, setFilter] = useState(null);

    const teams = useMemo(() => filter ? data?.teams?.filter((team: any) => team.owner?.toLowerCase()?.match(filter)) : data?.teams, [data, filter])

    const columns: GridColDef[] = [
        {
            field: 'id',
            headerName: 'ID',
            width: 100,
            editable: false,
            filterable: false
        },
        {
            field: 'team_name',
            headerName: 'Team Name',
            width: 250,
            editable: false,
            filterable: false
        },
        {
            field: 'owner',
            headerName: 'Team Owner',
            width: 250,
            editable: false,
            filterable: false
        },
        {
            field: 'organization',
            headerName: 'Team Org.',
            width: 250,
            editable: false,
            filterable: false
        }
    ]

    return (
        <>
            <Box p={2}>
                <React.Suspense fallback={<LoadingDotBox/>}>
                    {invitation && (
                        <Box mb={2}>
                            <Invitation invitation={invitation} close={() => [refetch(), refetchCustomerSegment()]}/>
                        </Box>
                    )}
                </React.Suspense>
                <Box mb={2}>
                    <Utils.LayoutUtils.MainInformationFact>
                        <Box mb={2}>
                            <Typography fontSize={14} fontWeight={700} sx={{mb: 0.5}}>Team</Typography>
                            <Typography fontSize={14} color={grey[700]}>
                                View and manage teams you're belongs to.
                            </Typography>
                        </Box>

                        <Typography fontSize={20} fontWeight={600}>{teams?.length?.toLocaleString() || 0}</Typography>
                    </Utils.LayoutUtils.MainInformationFact>
                </Box>
                <Box mt={2}>
                    <Box mb={2}>
                        <Utils.ObjectFilter callback={setFilter} label="Find teams by email"/>
                    </Box>
                    <DataGridSimpleStyled rows={teams} columns={columns} hideFooter/>
                </Box>
            </Box>
        </>
    )
}